import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme, CssBaseline, GlobalStyles } from '@mui/material';
import * as Sentry from '@sentry/react';
import { queryClient } from './lib/queryClient';
import { ErrorBoundary } from './components/common/ErrorBoundary';

// Centralized Theme - Edit src/theme/colors.ts to change colors
import { palette } from './theme/colors';

// Layout
import { MainLayout } from './components/layout/MainLayout';

// Pages
import { HomePage } from './pages/home/HomePage';
import { StoresPage } from './pages/stores/StoresPage';
import { StoreDetailPage } from './pages/stores/StoreDetailPage';
import { ProductsPage } from './pages/products/ProductsPage';
import { ProductDetailPage } from './pages/products/ProductDetailPage';
import { CartPage } from './pages/cart/CartPage';
import { CheckoutPage } from './pages/checkout/CheckoutPage';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { AccountSettingsPage } from './pages/AccountSettingsPage';
import { MyReservationsPage } from './pages/reservations/MyReservationsPage';
import { TrackReservationPage } from './pages/reservations/TrackReservationPage';
import { B2BAccessRequestsPage } from './pages/account/B2BAccessRequestsPage';

// B2B Portal Pages
import {
  B2BPortalLayout,
  B2BLandingPage,
  B2BAccessRequestPage,
  B2BProductsPage,
  B2BProductDetailPage,
  B2BCartPage,
  B2BQuotesPage,
  B2BQuoteDetailPage,
  B2BReservationsPage,
  B2BInvoicesPage,
  B2BAccountPage,
} from './pages/b2b';

/**
 * MUI Theme Configuration
 * Colors are imported from src/theme/colors.ts for easy customization
 */
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: palette.primary,
      light: palette.primaryLight,
      dark: palette.primaryDark,
      contrastText: palette.textOnPrimary,
    },
    secondary: {
      main: palette.accent,
      light: palette.accentLight,
      dark: palette.accentDark,
      contrastText: palette.textOnAccent,
    },
    background: {
      default: palette.background,
      paper: palette.surface,
    },
    text: {
      primary: palette.textPrimary,
      secondary: palette.textSecondary,
    },
    error: {
      main: palette.error,
      light: palette.errorLight,
    },
    warning: {
      main: palette.warning,
      light: palette.warningLight,
    },
    success: {
      main: palette.success,
      light: palette.successLight,
    },
    divider: palette.border,
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
    body1: {
      lineHeight: 1.6,
    },
    body2: {
      lineHeight: 1.5,
      color: palette.textSecondary,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 8px rgba(45, 52, 54, 0.04)',
    '0px 4px 16px rgba(45, 52, 54, 0.06)',
    '0px 8px 24px rgba(45, 52, 54, 0.08)',
    '0px 12px 32px rgba(45, 52, 54, 0.10)',
    '0px 16px 40px rgba(45, 52, 54, 0.12)',
    '0px 20px 48px rgba(45, 52, 54, 0.14)',
    '0px 24px 56px rgba(45, 52, 54, 0.16)',
    '0px 28px 64px rgba(45, 52, 54, 0.18)',
    '0px 32px 72px rgba(45, 52, 54, 0.20)',
    '0px 36px 80px rgba(45, 52, 54, 0.22)',
    '0px 40px 88px rgba(45, 52, 54, 0.24)',
    '0px 44px 96px rgba(45, 52, 54, 0.26)',
    '0px 48px 104px rgba(45, 52, 54, 0.28)',
    '0px 52px 112px rgba(45, 52, 54, 0.30)',
    '0px 56px 120px rgba(45, 52, 54, 0.32)',
    '0px 60px 128px rgba(45, 52, 54, 0.34)',
    '0px 64px 136px rgba(45, 52, 54, 0.36)',
    '0px 68px 144px rgba(45, 52, 54, 0.38)',
    '0px 72px 152px rgba(45, 52, 54, 0.40)',
    '0px 76px 160px rgba(45, 52, 54, 0.42)',
    '0px 80px 168px rgba(45, 52, 54, 0.44)',
    '0px 84px 176px rgba(45, 52, 54, 0.46)',
    '0px 88px 184px rgba(45, 52, 54, 0.48)',
    '0px 92px 192px rgba(45, 52, 54, 0.50)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          padding: '10px 24px',
          fontSize: '0.9rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
            transition: 'transform 0.2s ease',
          },
        },
        containedPrimary: {
          backgroundColor: palette.primary,
          color: palette.textOnPrimary,
          '&:hover': {
            backgroundColor: palette.primaryDark,
          },
        },
        containedSecondary: {
          backgroundColor: palette.accent,
          color: palette.textOnAccent,
          '&:hover': {
            backgroundColor: palette.accentDark,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 2px 12px rgba(45, 52, 54, 0.04)',
          border: `1px solid ${palette.border}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0px 8px 32px rgba(45, 52, 54, 0.08)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: palette.sky,
          color: palette.textPrimary,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: palette.surface,
            '& fieldset': {
              borderColor: palette.border,
            },
            '&:hover fieldset': {
              borderColor: palette.textMuted,
            },
            '&.Mui-focused fieldset': {
              borderColor: palette.primary,
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: palette.surface,
          color: palette.textPrimary,
          boxShadow: 'none',
          borderBottom: `1px solid ${palette.border}`,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: palette.skyLight,
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        colorError: {
          backgroundColor: palette.accent,
          color: palette.textOnAccent,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: palette.successLight,
          color: palette.success,
        },
        standardError: {
          backgroundColor: palette.errorLight,
          color: palette.error,
        },
        standardWarning: {
          backgroundColor: palette.warningLight,
          color: palette.warning,
        },
      },
    },
  },
});

// Global styles for fonts and smooth scrolling
const globalStyles = (
  <GlobalStyles
    styles={{
      html: {
        scrollBehavior: 'smooth',
      },
      body: {
        overflowX: 'hidden',
      },
      '::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '::-webkit-scrollbar-track': {
        background: palette.borderLight,
        borderRadius: '4px',
      },
      '::-webkit-scrollbar-thumb': {
        background: palette.textMuted,
        borderRadius: '4px',
        '&:hover': {
          background: palette.textSecondary,
        },
      },
    }}
  />
);

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong</p>}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {globalStyles}
          <ErrorBoundary>
            <BrowserRouter>
            <Routes>
            <Route element={<MainLayout />}>
              <Route path="/" element={<HomePage />} />

              <Route path="/stores" element={<StoresPage />} />
              <Route path="/stores/:id" element={<StoreDetailPage />} />

              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:id" element={<ProductDetailPage />} />

              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />

              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/account-settings" element={<AccountSettingsPage />} />
              <Route path="/account/b2b-access" element={<B2BAccessRequestsPage />} />

              <Route path="/track-reservation" element={<TrackReservationPage />} />
              <Route path="/my-reservations" element={<MyReservationsPage />} />
            </Route>

            {/* B2B Portal Routes */}
            <Route path="/b2b/:orgSlug" element={<B2BPortalLayout />}>
              <Route index element={<B2BLandingPage />} />
              <Route path="request-access" element={<B2BAccessRequestPage />} />
              <Route path="products" element={<B2BProductsPage />} />
              <Route path="products/:productId" element={<B2BProductDetailPage />} />
              <Route path="cart" element={<B2BCartPage />} />
              <Route path="quotes" element={<B2BQuotesPage />} />
              <Route path="quotes/:quoteId" element={<B2BQuoteDetailPage />} />
              <Route path="reservations" element={<B2BReservationsPage />} />
              <Route path="invoices" element={<B2BInvoicesPage />} />
              <Route path="account" element={<B2BAccountPage />} />
            </Route>
            </Routes>
          </BrowserRouter>
          </ErrorBoundary>
        </ThemeProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
